import {ROUTE_PATHS} from 'constant';

const SIDEBAR_ORIGIN_SCOPE_TYPES = [
  'my_workstreams',
  'team',
  'company',
  'pillar',
  'person',
  'direct_reports',
] as const;

type SidebarOriginScopeType = (typeof SIDEBAR_ORIGIN_SCOPE_TYPES)[number];
type SidebarOriginMode = 'planning';
type SidebarOriginState = {
  origin_scope_type?: SidebarOriginScopeType;
  origin_scope_value?: string | null;
  origin_mode?: SidebarOriginMode;
  origin_timeframe?: string;
};

const parseSidebarOriginScopeType = (value: unknown): SidebarOriginScopeType | null =>
  typeof value === 'string'
    ? (SIDEBAR_ORIGIN_SCOPE_TYPES as readonly string[]).includes(value.trim())
      ? (value.trim() as SidebarOriginScopeType)
      : null
    : null;

const parseSidebarOriginMode = (value: unknown): SidebarOriginMode | null =>
  value === 'planning' ? 'planning' : null;

/** True when `path` is the company trackers list on v2 (/workstreams?scope=company[&…]). */
export const isWorkstreamCompanyIndexPath = (path: string): boolean => {
  const q = path.indexOf('?');
  const pathname = q === -1 ? path : path.slice(0, q);
  if (pathname !== ROUTE_PATHS.WORKSTREAMS) return false;
  const params = new URLSearchParams(q === -1 ? '' : path.slice(q + 1));
  return params.get('scope') === 'company';
};

export const getLocationPathWithSearch = ({
  pathname,
  search = '',
  hash = '',
}: {
  pathname: string;
  search?: string;
  hash?: string;
}): string => `${pathname}${search}${hash}`;

export const readSidebarOriginState = (
  state: unknown
): {
  originScopeType: SidebarOriginScopeType | null;
  originScopeValue: string | null;
  originMode: SidebarOriginMode | null;
  originTimeframe: string | null;
} => {
  if (!state || typeof state !== 'object') {
    return {
      originScopeType: null,
      originScopeValue: null,
      originMode: null,
      originTimeframe: null,
    };
  }

  const originState = state as Record<string, unknown>;
  const rawOriginScopeValue = originState.origin_scope_value;
  const rawOriginTimeframe = originState.origin_timeframe;

  return {
    originScopeType: parseSidebarOriginScopeType(originState.origin_scope_type),
    originScopeValue: typeof rawOriginScopeValue === 'string' ? rawOriginScopeValue : null,
    originMode: parseSidebarOriginMode(originState.origin_mode),
    originTimeframe:
      typeof rawOriginTimeframe === 'string' && rawOriginTimeframe.trim().length > 0
        ? rawOriginTimeframe.trim()
        : null,
  };
};

export const resolveScopeNavigationMode = ({
  search = '',
  state,
}: {
  search?: string;
  state?: unknown;
}): SidebarOriginMode | null => {
  const params = new URLSearchParams(search);
  if (params.get('mode') === 'planning') {
    return 'planning';
  }

  const {originMode} = readSidebarOriginState(state);
  return originMode;
};

export const buildSidebarOriginState = ({
  search = '',
  state,
}: {
  search?: string;
  state?: unknown;
}): SidebarOriginState => {
  const {originScopeType, originScopeValue, originMode, originTimeframe} =
    readSidebarOriginState(state);
  if (originScopeType) {
    const nextState =
      originScopeValue === null
        ? {origin_scope_type: originScopeType}
        : {
            origin_scope_type: originScopeType,
            origin_scope_value: originScopeValue,
          };
    return originMode === 'planning'
      ? {
          ...nextState,
          origin_mode: originMode,
          ...(originScopeType === 'company' || originScopeType === 'pillar'
            ? originTimeframe
              ? {origin_timeframe: originTimeframe}
              : {}
            : {}),
        }
      : {
          ...nextState,
          ...(originScopeType === 'company' || originScopeType === 'pillar'
            ? originTimeframe
              ? {origin_timeframe: originTimeframe}
              : {}
            : {}),
        };
  }

  const params = new URLSearchParams(search);
  const scopeType = parseSidebarOriginScopeType(params.get('scope'));
  if (!scopeType) {
    return {};
  }

  const scopeValue = params.get('value');
  const nextState =
    scopeValue === null
      ? {origin_scope_type: scopeType}
      : {
          origin_scope_type: scopeType,
          origin_scope_value: scopeValue,
        };
  const mode = parseSidebarOriginMode(params.get('mode'));
  const timeframe = params.get('timeframe')?.trim() || null;
  const timeframeState =
    (scopeType === 'company' || scopeType === 'pillar') && timeframe
      ? {origin_timeframe: timeframe}
      : {};
  return mode === 'planning'
    ? {
        ...nextState,
        origin_mode: mode,
        ...timeframeState,
      }
    : {
        ...nextState,
        ...timeframeState,
      };
};

export const getWorkstreamDetailPath = ({id}: {id: number | string; pathname?: string}): string =>
  ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', id.toString());
